import { useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { client } from '../../hooks/Client'
import { useAnonymousResearchSignals } from '../../StateManagment/MeasurementSignalsContext'
import selectAnonymousResearch from '../../helpers/measurementSignals/selectAnonymousResearch'
import useMomentMatrixColumns from '../../hooks/measurementSignals/useMomentMatrixColumns'
import Table from '../common/table/Table'
import TableCell from '../common/table/TableCell'
import StaticCardLabel from './StaticCardLabel'
import AnonymousMomentCell from './AnonymousMomentCell'
import { buildShareQuestionnairePath } from '../../helpers/questionnaires/shareQuestionnairePath'

// AI-CONTEXT: See docs/patterns.md#dashboard-static-card
// AI-CONTEXT: See docs/features/meetmoment-signalering.md

/** De kolom vóór de meetmomenten. */
const LEADING_COLUMNS = [
    { key: 'research', label: 'ONDERZOEK' },
]

/** En de knop erachter. */
const TRAILING_COLUMNS = [
    { key: 'action', label: '', searchable: false },
]

/**
 * Welke groepsmeting staat stil?
 *
 * Dezelfde matrix als 'Aankomende meetmomenten', maar voor onderzoeken zonder
 * herleidbare invullers. Daar bestaat 'is Jan toe aan zijn tweede meting' niet:
 * er is geen Jan. De rij is dus het onderzoek, en in het vakje staat hoeveel
 * antwoorden er per meetmoment binnen zijn — groen is antwoorden, rood is nul.
 *
 * Onder het eerste lege vakje staat hoe lang er al niets binnenkwam. Geen
 * aftelling naar een datum: zonder deelnemer is er geen startpunt, dus geen
 * termijn.
 *
 * Langst stilgevallen bovenaan; onderzoeken waar nog nooit iets binnenkwam
 * daaronder, want die hebben geen stilteduur om op te sorteren.
 */
const DashboardAnonymousResearchCard = () => {
    const { rows, loaded, unavailable } = useAnonymousResearchSignals()

    const items = useMemo(() => selectAnonymousResearch(rows), [rows])

    const { momentLabels, columns } = useMomentMatrixColumns({
        items, leading: LEADING_COLUMNS, trailing: TRAILING_COLUMNS,
    })

    // Stabiel, anders vervalt de zoekfilter-memo in Table bij elke render.
    const searchAccessor = useCallback((row) => row?.researchTitle || '', [])

    // Drie toestanden, en ze mogen niet door elkaar lopen. "Geen anonieme
    // onderzoeken" is een bewering aan de lezer, en die is onwaar zolang de
    // gegevens onderweg zijn of een read is geweigerd.
    // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
    if (!loaded) {
        return (
            <div>
                <StaticCardLabel title='Anonieme onderzoeken' />
                <p className='score-explain-card__placeholder'>Laden…</p>
            </div>
        )
    }

    return (
        <div>
            <StaticCardLabel title='Anonieme onderzoeken' />

            {unavailable.length > 0 && (
                <p className='measurement-signal-warning'>
                    Dit overzicht is onvolledig — {unavailable.join(', ')} kon niet gelezen
                    worden. Er kunnen meer onderzoeken stilliggen dan hier staan.
                </p>
            )}

            {items.length === 0 && unavailable.length === 0 ? (
                <p className='measurement-signal-note'>
                    Er staat geen groepsmeting open. Onderzoeken verschijnen hier zodra de
                    vragenlijst anoniem gedeeld is — zonder deelnemer of huishouden erin —
                    en er nog een meetmoment is dat niemand invulde.
                </p>
            ) : (
                <>
                    <p className='moment-matrix-legend'>
                        <span className='moment-matrix-legend-item'>
                            <span className='moment-matrix-dot moment-matrix-dot--number moment-matrix-dot--answers'>7</span>
                            keer ingevuld
                        </span>
                        <span className='moment-matrix-legend-item'>
                            <span className='moment-matrix-dot moment-matrix-dot--number moment-matrix-dot--none'>0</span>
                            nog niet ingevuld
                        </span>
                    </p>
                    <Table
                        columns={columns}
                        items={items}
                        getItemKey={(row) => row.key}
                        searchAccessor={searchAccessor}
                        searchPlaceholder='Zoeken op onderzoek'
                        emptyMessage={unavailable.length > 0
                            ? 'Onbekend — een deel van de gegevens kon niet gelezen worden.'
                            : 'Geen groepsmetingen die aandacht vragen.'}
                        renderRow={(row) => (
                            <>
                                <TableCell>{row.researchTitle || 'Naamloos onderzoek'}</TableCell>
                                {momentLabels.map((label, index) => {
                                    const moment = row.moments?.[index] || null
                                    return (
                                        <TableCell key={`${row.key}-moment-${index}`} className='moment-matrix-cell-wrap'>
                                            <AnonymousMomentCell
                                                moment={moment}
                                                isNext={Boolean(moment) && row.next?.ID === moment.ID}
                                                silentDays={row.silentDays}
                                            />
                                        </TableCell>
                                    )
                                })}
                                <TableCell>
                                    <span className='measurement-signal-actions'>
                                        {row.questionnaireId ? (
                                            <Link to={buildShareQuestionnairePath({ client, questionnaireID: row.questionnaireId, researchID: row.researchId })}>
                                                Vragenlijst delen
                                            </Link>
                                        ) : (
                                            <span className='measurement-signal-note'>Geen vragenlijst</span>
                                        )}
                                    </span>
                                </TableCell>
                            </>
                        )}
                    />
                </>
            )}
        </div>
    )
}

export default DashboardAnonymousResearchCard
